"use client";

import React, { useState } from "react";
import Link from "next/link";
import { siteConfig } from "@/config/site.config";

export const Subscription: React.FC = () => {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [mensajeError, setMensajeError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // Trampa anti-spam
    if (!aceptaTerminos) {
      setMensajeError("Debes aceptar la política de privacidad para suscribirte.");
      return;
    }
    if (!email || !nombre) {
      setMensajeError("Por favor completa tu nombre y correo.");
      return;
    }

    setStatus("loading");
    setMensajeError("");

    try {
      const formData = new FormData();
      formData.append("nombre", nombre);
      formData.append("email", email);
      formData.append("consentimiento_ley_29733", "Aceptado");
      formData.append("_subject", "Nueva Suscripción al Boletín — Instituto Copleston");
      formData.append("_captcha", "false");

      const res = await fetch(`https://formsubmit.co/ajax/${siteConfig.correoContacto}`, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        setStatus("success");
        setNombre("");
        setEmail("");
        setAceptaTerminos(false);
      } else {
        setStatus("error");
        setMensajeError("No pudimos registrar tu suscripción en este momento. Inténtalo de nuevo.");
      }
    } catch {
      setStatus("error");
      setMensajeError("Error de conexión. Por favor intenta más tarde.");
    }
  };

  return (
    <section id="suscripcion" className="relative py-24 md:py-32 px-6 md:px-12 bg-bg-secondary/30 border-t border-gold/15">
      <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
        <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
          Comunidad & Publicaciones
        </span>

        <h2 className="font-serif text-3xl md:text-5xl text-text-pure mt-4 tracking-wide font-light">
          {siteConfig.newsletter.titulo}
        </h2>

        <div className="w-12 h-[1px] bg-gold/50 mt-6 mb-8" />

        <p className="text-text-muted text-sm md:text-base font-light tracking-wide max-w-lg mb-10 leading-relaxed">
          {siteConfig.newsletter.descripcion}
        </p>

        {status === "success" ? (
          <div className="w-full p-6 rounded-xl bg-gold/10 border border-gold/30 text-gold text-sm animate-fade-in text-center">
            <span className="font-semibold block mb-1">¡Gracias por unirte a nuestra comunidad!</span>
            Te hemos registrado con éxito. Te avisaremos cuando publiquemos cada video, audiolibro, episodio o ensayo.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4 text-left">
            {/* Honeypot anti-spam oculto */}
            <input
              type="text"
              name="_honey"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Tu nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                className="w-full px-5 py-3.5 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm placeholder-text-muted/50 outline-none transition-all rounded font-sans font-light"
              />
              <input
                type="email"
                placeholder="Tu correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-5 py-3.5 bg-bg-main border border-gold/20 focus:border-gold text-text-pure text-sm placeholder-text-muted/50 outline-none transition-all rounded font-sans font-light"
              />
            </div>

            {/* Casilla obligatoria Ley 29733 */}
            <label className="flex items-start gap-3 mt-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={aceptaTerminos}
                onChange={(e) => setAceptaTerminos(e.target.checked)}
                required
                className="mt-1 h-4 w-4 rounded border-gold/30 bg-bg-main text-gold focus:ring-gold"
              />
              <span className="text-xs text-text-muted font-light leading-relaxed">
                {siteConfig.newsletter.consentimientoTexto}{" "}
                <Link href="/privacidad" className="text-gold underline hover:text-gold-light ml-1">
                  Leer política
                </Link>
              </span>
            </label>

            {mensajeError && (
              <p className="text-red-400 text-xs mt-1 text-center font-light">{mensajeError}</p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full sm:w-auto mx-auto mt-4 bg-gold text-bg-main hover:bg-gold-light hover:shadow-lg hover:shadow-gold/20 px-10 py-3.5 text-xs font-semibold uppercase tracking-widest transition-all duration-300 rounded disabled:opacity-50 active:scale-[0.98]"
            >
              {status === "loading" ? "Procesando..." : "Suscribirme al Boletín"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
